// Spec: specs/account/billing-info/functional_requirements.md §5, §9.7
//       specs/payments/prepay/functional_requirements.md §6.5

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { Stripe } from '@stripe/stripe-js'

import railsPostFetch from '@aytm/lib/common/rails-post-fetch'
import { loadStripeClient } from '@aytm/app/lib/stripeClient'

import { CardCaptureBinding, CardCaptureConfirm, CardCaptureResult, StripeContextType } from '../types'

const CAPTURE_SESSION_URL = '/billing-info/capture_session'

export const CAPTURE_UNAVAILABLE_ERROR = 'Card entry is not ready yet. Please try again in a moment.'
export const CAPTURE_FAILED_ERROR = 'We could not save your card. Please try again.'
export const CAPTURE_SESSION_ERROR = 'Card entry is unavailable right now.'

type CaptureSessionResponse = { status: 'ok'; client_secret: string } | { status: 'fail'; errors?: string[] }

type StripeMode = 'add' | 'update'

type UseStripeOptions = {
  stripeKey: string
  email?: string
}

type UseStripeReturn = StripeContextType

/**
 * One card-capture slot: its own capture session and its own confirm, published by the one
 * card-entry mount it backs. A page carries as many slots as it has mount points — the Wallet
 * alone has the add form plus a row being edited — and a slot shared between two mounts binds
 * the capture to fields that have already gone away.
 */
const useCaptureSlot = (clientPromise: Promise<Stripe | null> | null, email: string): StripeContextType => {
  const [clientReady, setClientReady] = useState(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [sessionError, setSessionError] = useState<string | null>(null)
  // mirrors clientSecret so ensureSession stays stable and never reads a stale value
  const clientSecretRef = useRef<string | null>(null)
  const confirmRef = useRef<CardCaptureConfirm | null>(null)
  const sessionRequestRef = useRef<Promise<void> | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!clientPromise) return
    void clientPromise.then(client => {
      if (mountedRef.current) setClientReady(Boolean(client))
    })
  }, [clientPromise])

  const setSession = useCallback((secret: string | null) => {
    clientSecretRef.current = secret
    setClientSecret(secret)
  }, [])

  // A slot with no session can never capture a card, so the refusal is held rather than
  // swallowed: without it the mount point cannot tell a session still on its way from one that
  // failed, and shows a placeholder that never resolves (§5).
  const requestSession = useCallback(async () => {
    setSessionError(null)
    try {
      const response = await railsPostFetch(CAPTURE_SESSION_URL, { method: 'POST', body: '{}' })
      const json = (await response.json()) as CaptureSessionResponse
      if (!mountedRef.current) return

      if (json.status === 'ok') setSession(json.client_secret)
      else setSessionError(json.errors?.[0] || CAPTURE_SESSION_ERROR)
    } catch {
      if (mountedRef.current) setSessionError(CAPTURE_SESSION_ERROR)
    } finally {
      sessionRequestRef.current = null
    }
  }, [setSession])

  // Obtained when a flow opens rather than on page load, so a page nobody adds a card on
  // allocates no setup intent.
  const ensureSession = useCallback(() => {
    if (clientSecretRef.current || sessionRequestRef.current || !clientPromise) return
    sessionRequestRef.current = requestSession()
  }, [requestSession, clientPromise])

  const register = useCallback((confirm: CardCaptureConfirm) => {
    confirmRef.current = confirm
    return () => {
      if (confirmRef.current === confirm) confirmRef.current = null
    }
  }, [])

  const openCheckoutWith = useCallback(
    (_mode: StripeMode, onResult: (result: CardCaptureResult) => void) => {
      const confirm = confirmRef.current
      if (!confirm) {
        onResult({ ok: false, error: CAPTURE_UNAVAILABLE_ERROR })
        return
      }
      void confirm().then(
        result => {
          // A consumed session cannot capture a second card; a refused one can be retried as it is.
          if (result.ok && mountedRef.current) setSession(null)
          onResult(result)
        },
        // A throw spends nothing, so the session stands and the form can be submitted again;
        // without this branch the control simply stops, with no message anywhere.
        () => onResult({ ok: false, error: CAPTURE_FAILED_ERROR })
      )
    },
    [setSession]
  )

  const capture: CardCaptureBinding = useMemo(
    () => ({ clientPromise, clientSecret, email, sessionError, ensureSession, register }),
    [clientPromise, clientSecret, email, sessionError, ensureSession, register]
  )

  // memoised: consumers key their own callbacks off this slot, and a fresh object every
  // render rebuilds all of them
  return useMemo(
    () => ({ openCheckoutWith, isLoaded: clientReady && Boolean(clientSecret), capture }),
    [openCheckoutWith, clientReady, clientSecret, capture]
  )
}

/**
 * Shared provider client for a page. Owns the memoised browser client and a capture slot of
 * its own for any consumer that mounts the card entry straight off it.
 *
 * USAGE RULES:
 * - Call this hook ONLY ONCE in the parent component at the top of your feature tree
 * - Share the result with children (context, props, store - your choice)
 * - All children should use the shared instance from parent
 * - A child that renders its own card entry takes its own slot with `useCardCapture`
 */
export const useStripe = ({ stripeKey, email = '' }: UseStripeOptions): UseStripeReturn => {
  const clientPromise = useMemo(() => (stripeKey ? loadStripeClient(stripeKey) : null), [stripeKey])

  return useCaptureSlot(clientPromise, email)
}

/**
 * A capture slot of the caller's own, over the page's one browser client. Every consumer that
 * renders the card entry takes one, so its confirm lives and dies with the fields it published
 * and no other mount point can be captured through it.
 */
export const useCardCapture = (stripe: StripeContextType | null): StripeContextType | null => {
  const slot = useCaptureSlot(stripe?.capture.clientPromise ?? null, stripe?.capture.email ?? '')

  return stripe ? slot : null
}
